import React, { useRef } from "react"
import { motion, useScroll, useTransform } from "framer-motion"
import hitrustBadge from "@/assets/hitrust-badge.webp"
import hipaaBadge from "@/assets/hipaa-badge.svg"
import appStoreBadge from "@/assets/app-store-badge.png"
import googlePlayBadge from "@/assets/google-play-badge.png"

export function TrustSection() {
  const sectionRef = useRef(null)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  })
  
  const gradientX = useTransform(scrollYProgress, [0, 1], ["0%", "100%"])

  return (
    <section 
      ref={sectionRef}
      className="w-full py-20 max-md:py-14 relative overflow-hidden"
      aria-label="Trust and credentials"
    >
      {/* Parallax animated gradient background */}
      <motion.div 
        className="absolute inset-0"
        style={{ 
          background: "linear-gradient(135deg, hsl(var(--horizon)) 0%, hsl(var(--seaglass) / 0.3) 50%, hsl(var(--horizon)) 100%)",
          backgroundSize: "200% 200%",
          backgroundPositionX: gradientX
        }}
      />
      {/* Animated background shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, hsl(var(--beacon) / 0.4), transparent 70%)" }}
          animate={{ 
            scale: [1, 1.2, 1],
            x: [0, 30, 0],
            y: [0, 20, 0]
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute -bottom-48 -right-32 w-[500px] h-[500px] rounded-full opacity-15"
          style={{ background: "radial-gradient(circle, hsl(var(--seaglass) / 0.6), transparent 70%)" }}
          animate={{ 
            scale: [1.2, 1, 1.2],
            x: [0, -40, 0],
            y: [0, -30, 0]
          }}
          transition={{ 
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, hsl(var(--beacon) / 0.2), transparent 60%)" }}
          animate={{ 
            scale: [1, 1.1, 1],
            rotate: [0, 180, 360]
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>

      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-8 md:px-12 lg:px-20 3xl:px-28 4xl:px-32 relative z-10">
        <div className="grid grid-cols-3 gap-8 max-lg:grid-cols-1">
          {/* HITRUST Certified */}
          <motion.div 
            className="flex flex-col items-center text-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <img 
              src={hitrustBadge} 
              alt="HITRUST r2 Certified" 
              width={476} height={150}
              className="h-16 w-auto object-contain"
            />
            <p className="text-body text-anchor">
              HITRUST Certified for healthcare-grade security.
            </p>
          </motion.div>

          {/* HIPAA Compliant */}
          <motion.div 
            className="flex flex-col items-center text-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
          >
            <img 
              src={hipaaBadge} 
              alt="HIPAA Compliant" 
              width={150} height={150}
              className="h-16 w-auto object-contain"
            />
            <p className="text-body text-anchor">
              HIPAA Compliant to protect patient health information (PHI).
            </p>
          </motion.div>

          {/* App Store Rating */}
          <motion.div 
            className="flex flex-col items-center text-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
          >
            <div className="flex items-baseline gap-1">
              <span className="text-h1 text-anchor font-bold">4.9</span>
              <span className="text-h2 text-beacon">★</span>
            </div>
            <p className="text-body text-anchor">
              Across thousands of reviews
            </p>
            <div className="flex items-center gap-3">
              <a 
                href="https://apps.apple.com/us/app/grandpad/id904069946" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <img 
                  src={appStoreBadge} 
                  alt="Download on the App Store" 
                  className="h-10 w-auto object-contain"
                  width={240}
                  height={80}
                />
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=net.grandpad.puma&pcampaignid=web_share" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <img 
                  src={googlePlayBadge} 
                  alt="Get it on Google Play" 
                  className="h-10 w-auto object-contain"
                  width={270}
                  height={80}
                />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
